import { useRef, useState, useEffect, useCallback } from 'react';

interface FormValidationRefs {
    emailInputRef: React.RefObject<HTMLInputElement | null>;
    passwordInputRef: React.RefObject<HTMLInputElement | null>;
    checkboxRef: React.RefObject<HTMLInputElement | null>;
}

interface ValidationErrors {
    email: string;
    password: string;
    checkbox: string;
}

interface TouchedFields {
    email: boolean;
    password: boolean;
    checkbox: boolean;
}

interface UseFormValidationReturn {
    isFormValid: boolean;
    errors: ValidationErrors;
    touched: TouchedFields;
    refs: FormValidationRefs;
    handleInputChange: () => void;
    handleBlur: (field: keyof ValidationErrors) => void;
    handleFocus: (field: keyof ValidationErrors) => void;
    validateField: (field: keyof ValidationErrors, value: string | boolean) => string;
}

export function useFormValidation(): UseFormValidationReturn {
    const [isFormValid, setIsFormValid] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>({
        email: '',
        password: '',
        checkbox: '',
    });
    const [touched, setTouched] = useState<TouchedFields>({
        email: false,
        password: false,
        checkbox: false,
    });

    const emailInputRef = useRef<HTMLInputElement>(null);
    const passwordInputRef = useRef<HTMLInputElement>(null);
    const checkboxRef = useRef<HTMLInputElement>(null);

    const validateField = useCallback((field: keyof ValidationErrors, value: string | boolean): string => {
        switch (field) {
            case 'email': {
                if (!value || typeof value !== 'string') {
                    return 'Please enter your email address.';
                }
                if (value.trim() === '') {
                    return 'Please enter your email address.';
                }
                const emailRegex =
                    /^[^\s@]+@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
                if (!emailRegex.test(value)) {
                    return 'Please enter a valid email address.';
                }
                return '';
            }

            case 'password':
                if (!value || typeof value !== 'string') {
                    return 'Please enter your password.';
                }
                if (value.trim() === '') {
                    return 'Please enter your password.';
                }
                if (value.length < 6) {
                    return 'Please enter at least 6 characters.';
                }
                return '';

            case 'checkbox':
                if (!value) {
                    return 'This field is required.';
                }
                return '';

            default:
                return '';
        }
    }, []);

    const handleFocus = useCallback((field: keyof ValidationErrors) => {
        // Called on focus to mark the field as touched (tracks user interaction), but does not validate yet
        setTouched((prev) => ({
            ...prev,
            [field]: true,
        }));
    }, []);

    const handleBlur = useCallback(
        (field: keyof ValidationErrors) => {
            const input =
                field === 'checkbox'
                    ? checkboxRef.current
                    : field === 'email'
                      ? emailInputRef.current
                      : passwordInputRef.current;

            if (input) {
                const value = field === 'checkbox' ? input.checked : input.value;
                const error = validateField(field, value);

                // Mark as touched and set error on blur
                setTouched((prev) => ({
                    ...prev,
                    [field]: true,
                }));

                setErrors((prev) => ({
                    ...prev,
                    [field]: error,
                }));
            }
        },
        [validateField],
    );

    const checkFormValidity = useCallback(() => {
        const emailInput = emailInputRef.current;
        const passwordInput = passwordInputRef.current;
        const checkbox = checkboxRef.current;

        if (emailInput && checkbox) {
            const emailError = validateField('email', emailInput.value);
            const checkboxError = validateField('checkbox', checkbox.checked);

            // Password is only required if the field exists (not SSO flow)
            const passwordError = passwordInput ? validateField('password', passwordInput.value) : '';

            // Only update form validity for submit button, don't update error messages
            const isValid = !emailError && !passwordError && !checkboxError;
            setIsFormValid(isValid);
        }
    }, [validateField]);

    const handleInputChange = useCallback(() => {
        // Check form validity for submit button without showing validation errors
        checkFormValidity();
    }, [checkFormValidity]);

    // Check validity on component mount
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        checkFormValidity();
    }, [checkFormValidity]);

    return {
        isFormValid,
        errors,
        touched,
        refs: {
            emailInputRef,
            passwordInputRef,
            checkboxRef,
        },
        handleInputChange,
        handleBlur,
        handleFocus,
        validateField,
    };
}
